/**
 * VoiceRecorder — WhatsApp-style press-and-hold mic button.
 *
 * Records audio via MediaRecorder, uploads to /api/uploads?kind=audio,
 * then calls `onSent({ url, duration_sec, content_type })`.
 *
 * UX contract:
 *   • Press the mic → recording starts, a red pulse + timer + cancel-hint appear.
 *   • Release the mic → recording stops → upload → onSent fires.
 *   • Drag away (or press cancel) → recording is discarded.
 *   • 5-minute hard cap — stops automatically at 300s.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, X, Loader2 } from "lucide-react";

const MAX_SECONDS = 300; // 5 min

// Pick the best MediaRecorder mime type per browser
function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",                    // iOS Safari records to MP4/AAC
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

export default function VoiceRecorder({ onSent, disabled = false }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const stopReasonRef = useRef("send"); // "send" | "cancel"
  const timerRef = useRef(null);

  useEffect(() => () => {
    // cleanup on unmount
    try { recRef.current?.stop(); } catch { /* ignore */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const start = async (e) => {
    e?.preventDefault?.();
    if (disabled || state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = handleStop;
      rec.start();
      recRef.current = rec;
      stopReasonRef.current = "send";
      startedAtRef.current = Date.now();
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setSeconds(s);
        if (s >= MAX_SECONDS) stopAndSend();
      }, 200);
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "الرجاء السماح بالوصول للميكروفون من إعدادات المتصفح"
        : "تعذر بدء التسجيل — تحقق من الميكروفون";
      toast.error(msg);
    }
  };

  const stopAndSend = () => {
    if (state !== "recording") return;
    stopReasonRef.current = "send";
    try { recRef.current?.stop(); } catch { /* ignore */ }
  };

  const cancel = () => {
    if (state !== "recording") return;
    stopReasonRef.current = "cancel";
    try { recRef.current?.stop(); } catch { /* ignore */ }
  };

  const handleStop = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    const duration = Math.max(1, Math.floor((Date.now() - startedAtRef.current) / 1000));

    if (stopReasonRef.current === "cancel" || chunksRef.current.length === 0) {
      setState("idle");
      setSeconds(0);
      return;
    }
    setState("uploading");
    try {
      const mime = recRef.current?.mimeType || "audio/webm";
      const ext = mime.includes("mp4") ? "m4a"
                : mime.includes("ogg") ? "ogg"
                : "webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      const fd = new FormData();
      fd.append("file", blob, `voice-${Date.now()}.${ext}`);
      fd.append("kind", "audio");
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absUrl = data.url?.startsWith("http")
        ? data.url
        : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onSent?.({ url: absUrl, duration_sec: duration, content_type: mime });
    } catch (e) {
      toast.error("تعذر رفع الملف الصوتي — حاول مجدداً");
    } finally {
      setState("idle");
      setSeconds(0);
    }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(1, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (state === "uploading") {
    return (
      <button
        type="button"
        disabled
        className="h-11 w-11 rounded-full grid place-items-center bg-secondary shrink-0"
        data-testid="voice-uploading"
        title="جاري الإرسال…"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </button>
    );
  }

  if (state === "recording") {
    return (
      <div className="flex items-center gap-2 shrink-0" data-testid="voice-recording">
        <button
          type="button"
          onClick={cancel}
          className="h-11 w-11 rounded-full grid place-items-center border border-destructive/40 text-destructive hover:bg-destructive/10"
          title="إلغاء"
          data-testid="voice-cancel"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="text-xs font-mono text-destructive flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
          {mmss(seconds)}
        </div>
        <button
          type="button"
          onClick={stopAndSend}
          className="h-11 w-11 rounded-full grid place-items-center bg-primary text-primary-foreground hover:brightness-110"
          title="إرسال"
          data-testid="voice-send"
        >
          <Mic className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className="h-11 w-11 rounded-full grid place-items-center border border-border hover:bg-secondary shrink-0 disabled:opacity-40"
      title="اضغط للتسجيل"
      data-testid="voice-record-btn"
    >
      <Mic className="h-4 w-4" />
    </button>
  );
}
